import {Branded} from 'io-ts';
import {CurrencyCode} from '../Currency/CurrencyCode';
import {NonNegativeDoubleString} from '../NonNegativeDoubleString';
import {
    createNumberRepresentation as createRepresentation,
    NonNegativeDoubleStringRepresentation,
} from '../NonNegativeDoubleStringRepresentation';
import {NonNegativeInt} from '../NonNegativeInt';
import {Money} from './Money';

interface CurrencyAmountRepresentation {
    readonly currencyCode: CurrencyCode;
    readonly minimalDecimals: NonNegativeInt;
    readonly maximalDecimals: NonNegativeInt;
    readonly amount: NonNegativeDoubleStringRepresentation;
}

interface MoneyRepresentationBrand {
    readonly MoneyRepresentation: unique symbol;
}

export type MoneyRepresentation = Branded<CurrencyAmountRepresentation, MoneyRepresentationBrand>;

export const createMoneyRepresentation = (
    amount: NonNegativeDoubleString,
    currencyCode: CurrencyCode,
    minimalDecimals: NonNegativeInt,
    maximalDecimals: NonNegativeInt,
    locale: string
): MoneyRepresentation => {
    const representation = {
        currencyCode,
        minimalDecimals,
        maximalDecimals,
        amount: createRepresentation(amount, minimalDecimals, maximalDecimals, locale),
    };
    return representation as MoneyRepresentation;
};

export const fromMoney = (
    {currencyCode, decimals, amount}: Money,
    locale: string,
    minimalDecimals?: NonNegativeInt,
    maximalDecimals?: NonNegativeInt
): MoneyRepresentation => {
    const resultMinimalDecimals = minimalDecimals ?? decimals;
    const resultMaximalDecimals = maximalDecimals ?? decimals;
    const representation = {
        currencyCode,
        minimalDecimals: resultMinimalDecimals,
        maximalDecimals: resultMaximalDecimals,
        amount: createRepresentation(amount, resultMinimalDecimals, resultMaximalDecimals, locale),
    };
    return representation as MoneyRepresentation;
};

export const formatCurrency = ({currencyCode, amount}: MoneyRepresentation): string => `${amount} ${currencyCode}`;

export const representationAmountToString = (value: NonNegativeDoubleStringRepresentation) =>
    value.replace(/[^.\d]/g, '');
